import React, { useState, useCallback } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { TrendingUp, RefreshCw, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { runMonteCarloSimulation, formatCurrency } from '../utils/monteCarlo';

// Helper to format decimal age to years/months/days
const formatRetirementAge = (decimalAge) => {
  if (!decimalAge || isNaN(decimalAge)) return 'Not set';
  const years = Math.floor(decimalAge);
  const fractionalYear = decimalAge - years;
  const months = Math.floor(fractionalYear * 12);
  const days = Math.floor((fractionalYear * 12 - months) * 30);
  return `${years}y ${months}m ${days}d`;
};

// Custom tooltip for the fan chart
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm">
      <div className="font-semibold text-gray-900 mb-2">Age {label}</div>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-green-600">90th %ile (Best):</span>
          <span className="font-medium">{formatCurrency(data.p90)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-blue-600">75th %ile:</span>
          <span className="font-medium">{formatCurrency(data.p75)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-blue-600 font-semibold">Median:</span>
          <span className="font-bold">{formatCurrency(data.p50)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-cyan-600">25th %ile:</span>
          <span className="font-medium">{formatCurrency(data.p25)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-red-600">10th %ile (Worst):</span>
          <span className="font-medium">{formatCurrency(data.p10)}</span>
        </div>
      </div>
    </div>
  );
};


// Statistics card
const StatCard = ({ label, value, icon: Icon, color, subtext }) => (
  <div className={`bg-${color}-50 rounded-lg p-3 border border-${color}-200`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 text-${color}-600`} />
      <span className="text-xs text-gray-600">{label}</span>
    </div>
    <div className={`text-lg font-bold text-${color}-700`}>{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

const MonteCarloChart = ({ vision, data, darkMode }) => {
  const [results, setResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);

  const runSimulation = useCallback(() => {
    setIsRunning(true);
    setError(null);

    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      try {
        // Pass the baseline financial data from the main app
        const simResults = runMonteCarloSimulation(data, vision, {
          numSimulations: 1000
        });
        setResults(simResults);
      } catch (err) {
        console.error('Monte Carlo error:', err);
        setError('Failed to run simulation. Please try again.');
      } finally {
        setIsRunning(false);
      }
    }, 50);
  }, [data, vision]);

  // Format Y axis
  const formatYAxis = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value}`;
  };

  return (
    <div className={`rounded-xl shadow-lg p-4 ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <TrendingUp className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            Monte Carlo Projections
          </h3>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            1,000 simulations with years-retired glide path (90% stocks pre-retirement, reducing post-retirement)
          </p>
        </div>
        <button
          onClick={runSimulation}
          disabled={isRunning}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
            isRunning
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`} />
          {isRunning ? 'Running...' : results ? 'Re-run' : 'Generate Projections'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className={`border rounded-lg p-3 mb-4 text-sm ${darkMode ? 'bg-red-900/30 border-red-700 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* No results yet */}
      {!results && !isRunning && (
        <div className={`rounded-xl p-8 text-center ${darkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <h4 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Run Monte Carlo Simulation
          </h4>
          <p className={`text-sm max-w-md mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Click "Generate Projections" to see how your portfolio might perform
            across 1,000 different market scenarios based on historical data.
          </p>
          <div className={`flex items-center justify-center gap-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <Info className="w-4 h-4" />
            <span>Results show probability ranges, not guarantees</span>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isRunning && (
        <div className={`rounded-xl p-8 text-center ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
          <RefreshCw className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-spin" />
          <h4 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Running 1,000 Simulations...
          </h4>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Calculating probability distributions
          </p>
        </div>
      )}

      {/* Results */}
      {results && !isRunning && (
        <>
          {/* Disclaimer */}
          <div className={`border rounded-lg p-2 mb-4 text-xs ${darkMode ? 'bg-sky-900/30 border-sky-700 text-sky-300' : 'bg-sky-50 border-sky-200 text-sky-800'}`}>
            <strong>Note:</strong> These are simulations based on historical patterns, not predictions.
            Past performance does not guarantee future results. Consult a financial advisor.
          </div>

          {/* Statistics row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="Success Rate (Age 85)"
              value={`${results.statistics.successRateAt85}%`}
              icon={results.statistics.successRateAt85 >= 80 ? CheckCircle : AlertTriangle}
              color={results.statistics.successRateAt85 >= 80 ? 'green' : results.statistics.successRateAt85 >= 60 ? 'yellow' : 'red'}
              subtext="Probability of not running out"
            />
            <StatCard
              label="Success Rate (Age 90)"
              value={`${results.statistics.successRateAt90}%`}
              icon={results.statistics.successRateAt90 >= 80 ? CheckCircle : AlertTriangle}
              color={results.statistics.successRateAt90 >= 80 ? 'green' : results.statistics.successRateAt90 >= 60 ? 'yellow' : 'red'}
              subtext="Extended longevity scenario"
            />
            <StatCard
              label="Median at Retirement"
              value={formatCurrency(results.statistics.medianAtRetirement)}
              icon={TrendingUp}
              color="blue"
              subtext={`At ${formatRetirementAge(results.parameters.retireAge)}`}
            />
            <StatCard
              label="Worst Case (10th %)"
              value={formatCurrency(results.statistics.worstCase10thPercentile)}
              icon={AlertTriangle}
              color="orange"
              subtext="20 years post-retirement"
            />
          </div>

          {/* Fan Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={results.percentileData}
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="age"
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />

                {/* Retirement line */}
                <ReferenceLine
                  x={results.parameters.retireAge}
                  stroke="#0ea5e9"
                  strokeDasharray="5 5"
                  label={{
                    value: 'Retire',
                    position: 'top',
                    fill: '#0ea5e9',
                    fontSize: 12
                  }}
                />

                {/* 10-90 percentile band (lightest) */}
                <Area
                  type="monotone"
                  dataKey="p90"
                  stroke="none"
                  fill="#93c5fd"
                  fillOpacity={0.3}
                  name="90th percentile"
                />
                <Area
                  type="monotone"
                  dataKey="p10"
                  stroke="none"
                  fill="#ffffff"
                  fillOpacity={1}
                  name="10th percentile"
                />

                {/* 25-75 percentile band (medium) */}
                <Area
                  type="monotone"
                  dataKey="p75"
                  stroke="none"
                  fill="#60a5fa"
                  fillOpacity={0.4}
                  name="75th percentile"
                />
                <Area
                  type="monotone"
                  dataKey="p25"
                  stroke="none"
                  fill="#ffffff"
                  fillOpacity={1}
                  name="25th percentile"
                />

                {/* Median line */}
                <Line
                  type="monotone"
                  dataKey="p50"
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={false}
                  name="Median"
                />

                {/* 10th percentile line (worst case) */}
                <Line
                  type="monotone"
                  dataKey="p10"
                  stroke="#06b6d4"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  name="10th %ile"
                />

                {/* 90th percentile line (best case) */}
                <Line
                  type="monotone"
                  dataKey="p90"
                  stroke="#22c55e"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  dot={false}
                  name="90th %ile"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mt-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 bg-blue-600"></div>
              <span className="text-gray-600">Median (50th %ile)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 bg-green-500 border-dashed"></div>
              <span className="text-gray-600">Best Case (90th)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 bg-cyan-500 border-dashed"></div>
              <span className="text-gray-600">Worst Case (10th)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-3 bg-blue-400 opacity-40 rounded"></div>
              <span className="text-gray-600">25th-75th Range</span>
            </div>
          </div>

          {/* Parameters info */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            <div className="font-medium mb-1">Simulation Parameters:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>Starting Portfolio: {formatCurrency(results.parameters.startingPortfolio)}</div>
              <div>Age Range: {results.parameters.startAge} - {results.parameters.endAge}</div>
              <div>Retire At: {formatRetirementAge(results.parameters.retireAge)}</div>
              <div>Stocks Now: {results.parameters.startingStockAllocation}% → At Retire: {results.parameters.retirementStockAllocation}%</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonteCarloChart;
